import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, LogIn } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { WEB_MAIN_COLOR, FONT_FAMILY } from '../../common/sharedFunctions';
import { colors } from '../Theme/WebTheme';

const navItems = [
  { key: 'home', path: '/' },
  { key: 'myaccount', path: '/bookings' },
  { key: 'about_us', path: '/about-us' },
  { key: 'contact_us', path: '/contact-us' },
];

const ModernLandingHeader = ({ changeColorOnScroll }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const navigate = useNavigate();
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const MAIN_COLOR = WEB_MAIN_COLOR(settings?.appCat);

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (changeColorOnScroll) {
        setScrolled(window.pageYOffset > changeColorOnScroll.height);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [changeColorOnScroll]);

  const isLoggedIn = auth?.profile?.uid;
  const role = auth?.profile?.usertype;

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
      style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={require("../../assets/img/gruwcer-logo.png")}
              alt="Gruwcer Delivery"
              className="h-10 w-auto"
              style={{ filter: scrolled ? 'none' : 'brightness(0) invert(1)' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                className={`text-sm font-medium transition-colors hover:opacity-80 ${
                  scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
                style={{ fontFamily: FONT_FAMILY }}
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>

          {/* Auth Button */}
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <button
                onClick={() => navigate(role === 'driver' ? '/bookings' : '/dashboard')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                  scrolled 
                    ? 'bg-gradient-to-r from-[#009689] to-[#319F44] text-white hover:from-[#007a6e] hover:to-[#267a35]'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                style={{ fontFamily: FONT_FAMILY }}
              >
                <User className="w-4 h-4" />
                {t('dashboard_text')}
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                  scrolled 
                    ? 'bg-gradient-to-r from-[#009689] to-[#319F44] text-white hover:from-[#007a6e] hover:to-[#267a35]'
                    : 'bg-white text-gray-800 hover:bg-gray-100'
                }`}
                style={{ fontFamily: FONT_FAMILY }}
              >
                <LogIn className="w-4 h-4" />
                {t('login')}
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-2xl"
          >
            {mobileOpen ? (
              <X className={scrolled ? 'w-6 h-6 text-gray-700' : 'w-6 h-6 text-white'} />
            ) : (
              <Menu className={scrolled ? 'w-6 h-6 text-gray-700' : 'w-6 h-6 text-white'} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white shadow-lg"
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-2xl font-medium"
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  {t(item.key)}
                </Link>
              ))}
              <div className="pt-2 border-t">
                {isLoggedIn ? (
                  <button
                    onClick={() => { setMobileOpen(false); navigate(role === 'driver' ? '/bookings' : '/dashboard'); }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#009689] to-[#319F44] text-white rounded-2xl font-medium"
                    style={{ fontFamily: FONT_FAMILY }}
                  >
                    <User className="w-4 h-4" />
                    {t('dashboard_text')}
                  </button>
                ) : (
                  <button
                    onClick={() => { setMobileOpen(false); navigate('/login'); }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#009689] to-[#319F44] text-white rounded-2xl font-medium"
                    style={{ fontFamily: FONT_FAMILY }}
                  >
                    <LogIn className="w-4 h-4" />
                    {t('login')}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default ModernLandingHeader;
